import React, { useMemo } from 'react';
import { uniqueArray } from 'shared/utils/unique';
import useFormatMessage from 'hooks/useFormatMessage';
import { getDimensionDescription, getDimensionLabel } from 'components/DimensionLabel';
import { fraction } from 'shared/constants/math';
import { CHART_COLORS } from 'shared/constants/colors';
import { getFormatter } from 'shared/utils/formatter';
import { getMetricPrecision } from 'shared/utils/metricPrecision';
import { metricSupportsShareOfTotal } from 'shared/utils/metricChange';
import { getDimensionValue } from 'components/DimensionValue';
import { useMatomoGoalNames } from 'hooks/useMatomoGoalNames';
import { useWebsiteCurrency } from 'hooks/useWebsiteCurrency';
import { getMetricDescription, getMetricLabel } from 'components/MetricLabel';
import Chart from './Chart';
import { metricsTooltipFormatter } from './tooltip';
import { getLegend, getRowId, getSegmentHtmlTitle, getSegmentTitle } from './utils';

const LineChartResult = ({ results, config, segments, area, selectedMetric }) => {
  const $t = useFormatMessage();
  const { goalNames, loading: goalNamesLoading } = useMatomoGoalNames();
  const { currency, ready } = useWebsiteCurrency();
  const displayCurrency = ready ? currency : null;

  const metrics = config?.metrics || [];
  const dimensions = config?.dimensions || [];
  const dimensionIds = dimensions.map(d => d.name);
  const seriesByDimensions = dimensions.length > 0;
  const seriesByMetrics = !seriesByDimensions;
  const shareOfTotal = config?.settings?.shareOfTotal && seriesByDimensions;
  const selectedMetricConfig = metrics.find(m => m.name === selectedMetric);
  const height = config?.settings?.lineChartHeight || 500;

  const option = useMemo(() => {
    const metricTypes = uniqueArray(metrics.map(metric => metric.type || 'number'));
    let paletteColorIndex = 0;

    const getSeries = ({ name, metric, segment, data, lineColor, subSeriesCount, ...rest }) => {
      const { id } = segment;
      const result = results?.find(r => r.segmentId === id);

      const dimensionPart = dimensions.map(d => getDimensionValue({ $t, value: data.find(d2 => getRowId(d2, dimensionIds) === name)?.[d.name], dimension: d, maxLength: 20, goalNames, goalNamesLoading })).join(' ');
      const segmentTitle = getSegmentTitle(segment);
      const label = seriesByMetrics
        ? (dimensionPart
          ? `${getMetricLabel({ $t, name: metric })} — ${segmentTitle} - ${dimensionPart}`
          : `${getMetricLabel({ $t, name: metric })} — ${segmentTitle}`)
        : (subSeriesCount > 1 && dimensionPart
          ? `${dimensionPart} — ${segmentTitle}`
          : `${segmentTitle} - ${dimensionPart}`);

      const description = [
        `<b>Segment:</b> ${getSegmentHtmlTitle(segment)}`,
        ...dimensions.flatMap(d => [
          `<b>${getDimensionLabel({ $t, name: d.name })}:</b> ${getDimensionValue({ $t, value: data.find(d2 => getRowId(d2, dimensionIds) === name)?.[d.name], dimension: d, goalNames, goalNamesLoading, html: true })}`,
          `<em>${getDimensionDescription({ $t, name: d.name })}</em>`,
        ]),
        (seriesByMetrics ? `<b>${getMetricLabel({ $t, name: metric })}:</b> ${getMetricDescription({ $t, name: metric })}` : ''),
      ].map(d => `<div>${d}</div>`).join('');

      return {
        name: `${id}-${name}`,
        label,
        description: `<div class="chart-tooltip">${description}</div>`,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 10,
        connectNulls: true,
        lineStyle: {
          color: lineColor,
        },
        itemStyle: {
          color: lineColor,
        },
        stack: area ? id : undefined,
        areaStyle: area ? {} : undefined,
        data: segment.dateRange.periods.map(period => {
          const date = period.start.format('YYYY-MM-DD');
          const periodData = data.find(d => d.date === date);
          let value = periodData?.[metric] || 0;
          const metricConfig = metrics.find(m => m.name === metric);
          if (shareOfTotal && metricSupportsShareOfTotal(metricConfig)) {
            const total = result?.data.filter(d2 => d2.date === date).reduce((acc, d2) => acc + d2[metric], 0);
            value = fraction(value, total);
          }
          return [date, value, id, periodData];
        }),
        ...rest,
      };
    };

    const series = segments?.flatMap(s => {
      const segmentResults = results?.find(r => r.segmentId === s.id);
      if (seriesByDimensions) {
        const dimensionValues = uniqueArray(segmentResults?.data.map(d => getRowId(d, dimensionIds))).slice(0, 25);
        const subCount = dimensionValues.length;
        return dimensionValues.map(name => getSeries({
          name,
          segment: s,
          data: segmentResults?.data.filter(d => getRowId(d, dimensionIds) === name) || [],
          metric: selectedMetric,
          lineColor: subCount > 1
            ? CHART_COLORS[paletteColorIndex++ % CHART_COLORS.length]
            : s.color,
        }));
      }
      if (seriesByMetrics) {
        const subCount = metrics.length;
        return metrics.map(metric => getSeries({
          name: metric.name,
          segment: s,
          data: segmentResults?.data || [],
          yAxisIndex: area ? undefined : metricTypes.indexOf(metric.type || 'number'),
          metric: metric.name,
          subSeriesCount: subCount,
          lineColor: subCount > 1
            ? CHART_COLORS[paletteColorIndex++ % CHART_COLORS.length]
            : s.color,
        }));
      }
      return [];
    });

    const getSeriesName = (name) => {
      const serie = series.find(s => s.name === name);
      return serie?.label || name;
    };

    const getSeriesDescription = (name) => {
      const serie = series.find(s => s.name === name);
      return serie?.description || '';
    };

    return {
      grid: {
        left: 20,
        right: 20,
        top: 50,
        bottom: 20,
        containLabel: true,
      },
      tooltip: {
        trigger: seriesByDimensions ? 'item' : 'axis',
        formatter: params => {
          const [date, , segmentId, data] = seriesByDimensions ? params.data : params[0].data;

          return metricsTooltipFormatter({
            $t,
            segments: seriesByDimensions ? segments.filter(s => s.id === segmentId) : segments,
            metrics,
            results,
            dimensions,
            date,
            rowId: getRowId(data, [...dimensionIds, 'date']),
            goalNames,
            goalNamesLoading,
            currency: displayCurrency,
          });
        },
      },
      legend: getLegend({ formatter: getSeriesName, tooltipFormatter: params => getSeriesDescription(params.name) }),
      yAxis: metricTypes.map((type) => {
        const metricForType = metrics.find(m => (m.type || 'number') === type);
        const axisShareOfTotal = shareOfTotal && metricSupportsShareOfTotal(
          seriesByDimensions ? selectedMetricConfig : metricForType,
        );
        return {
          type: 'value',
          axisLabel: {
            formatter: value => getFormatter(axisShareOfTotal ? 'percent' : type, {
              currency: displayCurrency,
              maximumFractionDigits: getMetricPrecision(metricForType, 'display'),
            })(value),
          },
          max: area && axisShareOfTotal ? 1 : null,
        };
      }),
      xAxis: {
        type: 'time',
      },
      series,
    };
  }, [results, metrics, segments, area, selectedMetric, dimensions, shareOfTotal, goalNames, goalNamesLoading, displayCurrency, $t, dimensionIds, seriesByDimensions, seriesByMetrics, selectedMetricConfig]);

  return (
    <Chart
      option={option}
      height={height}
    />
  );
};

export default LineChartResult;
